// Layer 4 (UI) — the route's card and in-card table grammar, per the locked
// comp (`.impeccable/mocks/decision/downtime.html` `.card`, `.t`): a matte
// GlassCard with its title inside, quieter than the figures it sits over,
// meta and actions on the right, rows divided by hairlines inside.
// Presentational only; derives nothing.

import type { ReactNode } from 'react';
import { CitationChip, GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';

export const SYN_CHIP = <CitationChip label="[syn]" confidence="inferred" />;

/** Wraps a design-system `Table` so it sits on the card, not in a second box:
 *  transparent wrapper and header, caps heads, comp cell rhythm. */
export const CARD_TABLE = cn(
  'min-w-0 overflow-x-auto',
  '[&>div]:rounded-none [&>div]:border-0 [&>div]:bg-transparent dark:[&>div]:bg-transparent',
  '[&_thead]:bg-transparent dark:[&_thead]:bg-transparent',
  '[&_th]:whitespace-nowrap [&_th]:px-2 [&_th]:py-1.5 [&_th]:font-semibold [&_th]:tracking-[0.07em]',
  '[&_td]:px-2 [&_td]:py-2 [&_td]:text-ink-secondary',
);

/** Same, one step denser (comp `.t.dense`). */
export const CARD_TABLE_DENSE = cn(CARD_TABLE, '[&_td]:py-1.5');

/** A hot row: the top-ranked or worst row of a table (comp `tr.hot`). */
export const HOT_ROW = 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] hover:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] dark:hover:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]';

export function DowntimeCard({
  title,
  meta,
  actions,
  className,
  children,
  'aria-label': ariaLabel,
}: {
  title?: string;
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
  'aria-label'?: string;
}) {
  return (
    <GlassCard as="section" tone="matte" aria-label={ariaLabel ?? title} className={cn('min-w-0 px-4 py-3', className)}>
      {title || meta || actions ? (
        <header className="mb-2.5 flex flex-wrap items-center gap-2">
          {title ? <h3 className="text-sm font-semibold text-ink-primary">{title}</h3> : null}
          <span className="flex-1" />
          {meta ? <span className="text-xs tabular-nums text-muted">{meta}</span> : null}
          {actions}
        </header>
      ) : null}
      {children}
    </GlassCard>
  );
}

/** A rank bar on the comp's track (`.hb`): crimson for the top-ranked row,
 *  ink-secondary for the rest, a soft ink for rows past a cut. */
export function RankBar({
  ratio,
  tone = 'ink',
  className,
}: {
  ratio: number;
  tone?: 'top' | 'ink' | 'dim' | 'ok' | 'warn' | 'bad';
  className?: string;
}) {
  const fill = {
    top: 'var(--color-accent-500)',
    ink: 'var(--ink-secondary)',
    dim: 'color-mix(in srgb, var(--ink-secondary) 22%, transparent)',
    ok: 'var(--state-online)',
    warn: 'var(--state-warning)',
    bad: 'var(--state-blocked)',
  }[tone];
  return (
    <span className={cn('relative block h-1.5 min-w-0 overflow-hidden rounded-full bg-[var(--hairline)]', className)}>
      <span
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${Math.min(100, Math.max(0, ratio * 100))}%`, backgroundColor: fill }}
      />
    </span>
  );
}

/** Around a `KpiStrip` of three cards: three equal columns across the row, as
 *  the comp draws them (`.kpis --n:3`), instead of three of the strip's four. */
export const THREE_UP = 'md:[&>section]:grid-cols-3';
